import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Menu, ArrowLeft, Search, X } from 'lucide-react';
import { useApp } from '../../context/AppContext';

// Sticky top-0 header per shared-docs/frontend-architecture.md. safe-area-top pushes
// content below the Dynamic Island on iOS. z-30 sits above content but below modals.
export default function Header() {
  const { setSidebarOpen, searchQuery, setSearchQuery } = useApp();
  const [showSearch, setShowSearch] = React.useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const searchRef = React.useRef(null);

  const isSubpage = location.pathname.split('/').filter(Boolean).length > 1;

  const titles = {
    '/': 'Explore',
    '/browse': 'Browse Recipes',
    '/cookbook': 'My Cookbook',
    '/submit': 'Submit Recipe',
    '/meal-plan': 'Meal Plan',
    '/grocery': 'Grocery List',
    '/profile': 'Profile',
  };

  const getTitle = () => {
    if (location.pathname.match(/^\/submit\/\d+\/edit$/)) return 'Edit Submission';
    if (location.pathname.match(/^\/recipes\/\d+\/cook$/)) return 'Cooking Mode';
    if (location.pathname.match(/^\/recipes\/\d+$/)) return 'Recipe';
    return titles[location.pathname] || 'Cooksmith';
  };

  React.useEffect(() => {
    if (showSearch && searchRef.current) searchRef.current.focus();
  }, [showSearch]);

  const showSearchIcon =
    location.pathname === '/browse' || location.pathname === '/' || location.pathname === '/cookbook';

  return (
    <header className="sticky top-0 z-30 safe-area-top bg-white dark:bg-night-surface border-b border-gray-100 dark:border-night-card">
      <div className="max-w-2xl mx-auto px-4 h-14 flex items-center gap-2">
        {isSubpage ? (
          <button
            onClick={() => navigate(-1)}
            aria-label="Go back"
            className="p-2 -ml-2 rounded-lg hover:bg-gray-100 dark:hover:bg-night-card text-gray-700 dark:text-night-text"
          >
            <ArrowLeft size={22} />
          </button>
        ) : (
          <button
            onClick={() => setSidebarOpen(true)}
            aria-label="Open menu"
            className="p-2 -ml-2 rounded-lg hover:bg-gray-100 dark:hover:bg-night-card text-gray-700 dark:text-night-text md:hidden"
          >
            <Menu size={22} />
          </button>
        )}

        <div className="flex-1 min-w-0">
          {showSearch ? (
            <div className="relative">
              <Search
                size={18}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-night-muted"
              />
              <input
                ref={searchRef}
                type="text"
                placeholder="Search recipes..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-9 py-2 rounded-lg bg-gray-100 dark:bg-night-card text-sm dark:text-night-text placeholder-gray-400 dark:placeholder-night-muted focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              {searchQuery && (
                <button
                  onClick={() => { setSearchQuery(''); setShowSearch(false); }}
                  aria-label="Clear search"
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-gray-500 dark:text-night-muted"
                >
                  <X size={16} />
                </button>
              )}
            </div>
          ) : (
            <h1 className="text-base font-semibold text-gray-900 dark:text-night-text truncate">
              {getTitle()}
            </h1>
          )}
        </div>

        {showSearchIcon && !showSearch && (
          <button
            onClick={() => setShowSearch(true)}
            aria-label="Search"
            className="p-2 -mr-2 rounded-lg hover:bg-gray-100 dark:hover:bg-night-card text-gray-700 dark:text-night-text"
          >
            <Search size={20} />
          </button>
        )}
        {showSearch && (
          <button
            onClick={() => { setShowSearch(false); setSearchQuery(''); }}
            aria-label="Close search"
            className="p-2 -mr-2 rounded-lg hover:bg-gray-100 dark:hover:bg-night-card text-gray-700 dark:text-night-text"
          >
            <X size={20} />
          </button>
        )}
      </div>
    </header>
  );
}
